import { QuestionVoice } from '@repo/lib/quiz/questions/class/QuestionVoice';
import { PencilLineIcon } from 'lucide-react';
import { useMemo } from 'react';

import {
  QuestionMarkedDescription,
  QuestionMarkedHeader,
  QuestionMarkedNoAnswer,
  QuestionMarkedQuestionContent,
  QuestionMarkedUserAnswer,
  QuestionMarkedWrapper,
} from '@/components/features/quiz/question/question-marked';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { AudioPlayer } from '@/components/ui/media-player';
import { useQuestionMarkedForm } from '@/hooks/questions';

import type { QuestionMarkedVoiceProps } from '.';

import { QuestionMarkedVoiceForm } from '.';

export function QuestionMarkedVoice(questionMarkedVoiceProps: QuestionMarkedVoiceProps) {
  const { marked, question, showMarked, showQuestion, answer, onEditMark } = questionMarkedVoiceProps;
  const { formRef, wrapperRef, openForm, setOpenForm, openFormHandler, closeFormHandler } = useQuestionMarkedForm();
  const questionObj = useMemo<QuestionVoice>(() => {
    const qClass = new QuestionVoice(question, null, marked);
    if (answer) qClass.importAnswer(answer);
    return qClass;
  }, [question, marked, answer]);

  const correctState = questionObj.correctState();
  const score: number = questionObj.getPoints();
  const totalPoints: number = questionObj.getMaxPoints();
  const correct: boolean = questionObj.getMarked()?.correct ?? false;
  const audioSrc: string = questionObj.getAnswer()?.answer ?? '';
  if (!showMarked) return null;

  /*
    Only a missing recording means "unanswered" — a recording still waiting for
    its manual grade has no feedback attached yet, and must not read as one.
  */
  if (!questionObj.isAnswered()) {
    return <QuestionMarkedNoAnswer showMarked={showMarked} />;
  }

  return (
    <QuestionMarkedWrapper showMarked={showMarked} ref={wrapperRef}>
      <QuestionMarkedHeader
        score={score}
        totalPoints={totalPoints}
        correct={correct}
        manualCheck={marked?.manualCheck ?? true}
        correctType={questionObj.correctState()}
        headerActions={
          onEditMark && (
            <Button
              variantColor={correctState === 'waiting' ? 'destructive' : 'default'}
              variant={correctState === 'waiting' ? 'secondary' : 'tertiary'}
              size="sm"
              disabled={openForm}
              onClick={openFormHandler}
            >
              <PencilLineIcon />
              Oceń
            </Button>
          )
        }
      />
      <QuestionMarkedQuestionContent showQuestion={showQuestion} content={question.content} />
      {questionObj.isAnswered() && !openForm && (
        <QuestionMarkedUserAnswer>
          <div className="space-y-2">
            <p className="text-accent-foreground text-sm font-medium">Odpowiedziano:</p>
            <div className="bg-card/50 rounded-md border p-2">
              <AudioPlayer src={audioSrc || ''} />
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-accent-foreground text-sm font-medium">Wyjaśnienie:</p>
            <p className="text-muted-foreground text-sm">{marked?.explanation ? marked.explanation : 'Brak wyjaśnienia'}</p>
          </div>
        </QuestionMarkedUserAnswer>
      )}
      <Collapsible open={openForm} onOpenChange={setOpenForm} className="-my-px">
        <CollapsibleContent ref={formRef}>
          <QuestionMarkedDescription />
          <QuestionMarkedVoiceForm onSubmit={onEditMark} onClose={closeFormHandler} marked={marked} maxPoints={totalPoints} answerSrc={audioSrc} isOpen={openForm} />
        </CollapsibleContent>
      </Collapsible>
    </QuestionMarkedWrapper>
  );
}
